'use client';

import { useEffect, useState } from 'react';
import { CheckCircle2, CircleHelp, Download, Sparkles, TriangleAlert } from 'lucide-react';
import { raskodirovat } from '@/lib/kursy/intonaciya';
import {
  proveritNaServere,
  rezhimProverki,
  v16k,
  vyvod,
  zagruzitPravila,
  zapomnitRezhim,
  type ItogProverki,
  type PravilaFrazy,
  type RezhimProverki,
  type VremyaSlova,
} from '@/lib/kursy/proverka';
import {
  RAZMER_MODELI_MB,
  modelSkachana,
  proveritVBrauzere,
  skachatModel,
} from '@/lib/kursy/proverka-brauzer';

type Sostoyanie =
  | { tip: 'net' }
  | { tip: 'vybor' }
  | { tip: 'skachat' }
  | { tip: 'kachaem'; dolya: number }
  | { tip: 'idet' }
  | { tip: 'gotovo'; itog: ItogProverki }
  | { tip: 'nedostupno'; pochemu: string };

const KNOPKA =
  'inline-flex items-center gap-2 rounded-full bg-[var(--color-accent-500)] px-4 py-2 font-semibold text-[var(--color-bg)] hover:bg-[var(--color-accent-400)]';
const KNOPKA_2 =
  'inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-4 py-2 hover:border-[var(--color-accent-500)]';

/**
 * Автопроверка ключевых звуков после записи. Только для фраз с правилами.
 * Человек сам выбирает, где проверять: на нашем сервере в РФ (запись не
 * хранится) или на устройстве (модель 122 МБ, запись никуда не уходит).
 */
export function ProverkaZapisi({
  kurs,
  urok,
  id,
  zapis,
  onItog,
}: {
  kurs: string;
  urok: string;
  id: string;
  zapis: Blob;
  /**
   * Итог для тренажёра: вердикт по звукам (null — проверки нет или она
   * недоступна) и где в записи слова — для графика по словам.
   */
  onItog?: (itog: ItogProverki | null, slova: VremyaSlova[] | undefined) => void;
}) {
  const [pravila, setPravila] = useState<PravilaFrazy | null>(null);
  const [s, setS] = useState<Sostoyanie>({ tip: 'net' });
  const [rezhim, setRezhim] = useState<RezhimProverki | null>(null);
  const [skachana, setSkachana] = useState(false);

  useEffect(() => {
    setRezhim(rezhimProverki());
    void modelSkachana().then(setSkachana);
    void zagruzitPravila(kurs).then((p) => {
      setPravila(p?.frazy[`${urok}/${id}`] ?? null);
    });
  }, [kurs, urok, id]);

  useEffect(() => {
    if (!pravila) return;
    if (!rezhim) {
      setS({ tip: 'vybor' });
      return;
    }
    if (rezhim === 'brauzer' && !skachana) {
      setS({ tip: 'skachat' });
      return;
    }
    // Объект, а не переменная: линтер не видит, что замыкание меняет её позже.
    const sostoyanie = { otmena: false };
    setS({ tip: 'idet' });
    void (async () => {
      const zvuk = await raskodirovat(zapis);
      if (!zvuk) {
        setS({ tip: 'nedostupno', pochemu: 'Не получилось прочитать запись.' });
        return;
      }
      const z16 = v16k(zvuk.dannye, zvuk.chastota);
      try {
        const otvet =
          rezhim === 'brauzer'
            ? await proveritVBrauzere(z16, pravila)
            : await proveritNaServere(z16, pravila);
        if (sostoyanie.otmena) return;
        if (!otvet) {
          setS({ tip: 'nedostupno', pochemu: 'Автопроверка сейчас недоступна.' });
          onItog?.(null, undefined);
          return;
        }
        const itog = vyvod(pravila, otvet.poteri, otvet.slyshit);
        setS({ tip: 'gotovo', itog });
        onItog?.(itog, otvet.slova);
      } catch {
        onItog?.(null, undefined);
        if (!sostoyanie.otmena)
          setS({
            tip: 'nedostupno',
            pochemu: 'Этот браузер не смог запустить проверку на устройстве.',
          });
      }
    })();
    return () => {
      sostoyanie.otmena = true;
    };
    // onItog — колбэк родителя, его смена не повод проверять заново.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pravila, rezhim, skachana, zapis]);

  const vybrat = (r: RezhimProverki | null) => {
    zapomnitRezhim(r);
    setRezhim(r);
  };

  const kachat = async () => {
    setS({ tip: 'kachaem', dolya: 0 });
    try {
      await skachatModel((dolya) => {
        setS({ tip: 'kachaem', dolya });
      });
      setSkachana(true);
    } catch {
      setS({
        tip: 'nedostupno',
        pochemu: 'Скачать не получилось. Попробуйте позже или проверяйте на сервере.',
      });
    }
  };

  if (!pravila || s.tip === 'net') return null;
  const zvuki = pravila.proverki.map((p) => p.zvuk).join(', ');
  // Фраза без проверки звуков тоже проверяется: узнаётся ли она целиком
  // (строгий режим держится на этом, замечание владельца 22.10).

  return (
    <div
      className="mb-5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 text-sm"
      aria-live="polite"
    >
      {s.tip === 'vybor' && (
        <div>
          <p className="mb-1 flex items-center gap-2 font-semibold">
            <Sparkles className="h-4 w-4 text-[var(--color-accent-400)]" aria-hidden="true" />
            Проверять произношение автоматически?
          </p>
          <p className="mb-3 text-[var(--color-text-muted)]">
            {zvuki
              ? `Компьютер проверит в этой фразе ${zvuki} и выровняет вашу мелодию по словам эталона.`
              : 'Компьютер выровняет вашу мелодию по словам эталона.'}{' '}
            Где проверять, выбираете вы: на нашем сервере в России — запись уходит туда и сразу
            удаляется, мы её не храним; или на этом устройстве — один раз скачать {RAZMER_MODELI_MB}{' '}
            МБ, лучше по Wi-Fi, и запись никуда не уходит.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                vybrat('server');
              }}
              className={KNOPKA}
            >
              Проверять на сервере
            </button>
            <button
              type="button"
              onClick={() => {
                vybrat('brauzer');
              }}
              className={KNOPKA_2}
            >
              На устройстве ({RAZMER_MODELI_MB} МБ)
            </button>
          </div>
        </div>
      )}
      {s.tip === 'skachat' && (
        <div>
          <p className="mb-3 text-[var(--color-text-muted)]">
            Для проверки на устройстве нужна модель: {RAZMER_MODELI_MB} МБ один раз, дальше работает
            и без интернета.
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void kachat()} className={KNOPKA}>
              <Download className="h-4 w-4" aria-hidden="true" /> Скачать модель
            </button>
            <button
              type="button"
              onClick={() => {
                vybrat('server');
              }}
              className={KNOPKA_2}
            >
              Лучше на сервере
            </button>
          </div>
        </div>
      )}
      {s.tip === 'kachaem' && (
        <div>
          <p className="mb-2 text-[var(--color-text-muted)]">
            Скачиваем модель… {Math.round(s.dolya * 100)}%
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-bg)]">
            <div
              className="h-full rounded-full bg-[var(--color-accent-500)]"
              style={{ width: `${String(Math.round(s.dolya * 100))}%` }}
            />
          </div>
        </div>
      )}
      {s.tip === 'idet' && (
        <p className="text-[var(--color-text-muted)]">
          {zvuki ? 'Проверяем звуки' : 'Проверяем фразу'}
          {rezhim === 'brauzer' ? ' на устройстве' : ''}…
        </p>
      )}
      {s.tip === 'nedostupno' && (
        <p className="text-[var(--color-text-muted)]">
          {s.pochemu} Сравните запись с эталоном на слух.
        </p>
      )}
      {s.tip === 'gotovo' && s.itog.tip === 'ne-ta-fraza' && (
        <p className="flex items-start gap-2">
          <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning-400)]" />
          Не похоже на эту фразу. Скажите её целиком и только потом нажмите «Стоп».
        </p>
      )}
      {s.tip === 'gotovo' && s.itog.tip === 'slova' && s.itog.slova.length === 0 && (
        <p className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-success-400)]" />
          Фраза узнана: сказана целиком и разборчиво. Отдельных звуков в ней не проверяем — мелодию
          сравните на графике.
        </p>
      )}
      {s.tip === 'gotovo' && s.itog.tip === 'slova' && s.itog.slova.length > 0 && (
        <ul className="space-y-2">
          {s.itog.slova.map((sl) => (
            <li key={sl.slovo} className="flex items-start gap-2">
              {sl.vyvod === 'poluchilos' && (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-success-400)]" />
              )}
              {sl.vyvod === 'ne-uveren' && (
                <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
              )}
              {sl.vyvod === 'oshibka' && (
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning-400)]" />
              )}
              <span>
                <span className="font-semibold" lang="en">
                  {sl.slovo}
                </span>{' '}
                — {sl.zvuk}:{' '}
                {sl.vyvod === 'poluchilos'
                  ? 'получилось.'
                  : sl.vyvod === 'ne-uveren'
                    ? 'не уверены, послушайте себя и эталон.'
                    : sl.podskazka}
              </span>
            </li>
          ))}
        </ul>
      )}
      {(s.tip === 'gotovo' || s.tip === 'idet' || s.tip === 'nedostupno') && rezhim && (
        <p className="mt-3 text-xs text-[var(--color-text-muted)]">
          Проверка смотрит только на эти звуки и иногда ошибается, оценку ставите вы. Проверяем{' '}
          {rezhim === 'server' ? 'на сервере' : 'на устройстве'} ·{' '}
          <button
            type="button"
            onClick={() => {
              vybrat(rezhim === 'server' ? 'brauzer' : 'server');
            }}
            className="underline hover:text-[var(--color-accent-400)]"
          >
            {rezhim === 'server' ? 'проверять на устройстве' : 'проверять на сервере'}
          </button>
        </p>
      )}
    </div>
  );
}
